// Shared types + activity-type metadata for the trip planner.

export type ActivityType =
  | "hike"
  | "food"
  | "hotel"
  | "travel"
  | "flight"
  | "train"
  | "boat"
  | "sightseeing"
  | "beach"
  | "nightlife"
  | "rest"
  | "other";

// Whether the activity needs a reservation (parking slot, lift ticket, permit…).
export type BookingStatus = "none" | "maybe" | "required";

// Lifecycle of a whole trip — colours the card on the planner landing page.
export type TripStatus = "wish" | "planned" | "done";
export const TRIP_STATUS: Record<TripStatus, { label: string; icon: string; color: string }> = {
  wish: { label: "Önskelista", icon: "💭", color: "#9b6dd6" },
  planned: { label: "Planerad", icon: "🗓️", color: "#e2b31a" },
  done: { label: "Genomförd", icon: "✅", color: "#3aa76d" },
};
export const TRIP_STATUS_ORDER: TripStatus[] = ["wish", "planned", "done"];
export const tripStatusOf = (s: any): TripStatus =>
  s === "wish" || s === "planned" || s === "done" ? s : "planned";

// An intermediate stop on a journey (layover airport, waypoint town…).
export interface Waypoint {
  location: string;
  lat: number | null;
  lng: number | null;
}

export interface Activity {
  id: string;
  title: string;
  type: ActivityType;
  time: string; // free text, e.g. "09:00" or "Morgon"
  location: string;
  lat: number | null; // geocoded coordinates (null if not picked from autocomplete)
  lng: number | null;
  endLocation: string; // hikes: end/destination point ("" for other types)
  endLat: number | null;
  endLng: number | null;
  stops?: Waypoint[]; // journeys: ordered intermediate stops / layovers
  travelTime: string; // free text, e.g. "10–15 min" (drive/travel time)
  travelAuto?: boolean; // travelTime was auto-computed (driving from base); user edits clear this
  travelFromOther?: boolean; // not travelling from the hotel — use a custom origin below
  originLocation?: string; // custom start point (when travelFromOther)
  originLat?: number | null;
  originLng?: number | null;
  parking: string; // free text parking notes
  booking: BookingStatus; // reservation needed?
  bookingInfo?: string; // what needs to be booked (shown in the Boka tab)
  booked?: boolean; // ticked off in the booking tab
  flightNumber?: string; // flight no. for flight activities (e.g. D84365)
  arrTime?: string; // flights: arrival time "HH:MM" (departure is in `time`)
  link: string; // optional URL (YouTube guide, info page…)
  notes: string;
  day: number | null; // 0-based day index, or null = idea pool (unassigned)
  span?: number; // how many consecutive days it lasts (>=1; 1 = single day)
  price?: number; // cost for this item in the trip currency (0/undefined = none)
}

// Days an activity occupies: [day, day+span). Idea-pool cards cover nothing.
export const activitySpan = (a: { span?: number }): number =>
  a.span && a.span > 1 ? a.span : 1;
export const coversDay = (a: { day: number | null; span?: number }, day: number): boolean =>
  a.day != null && day >= a.day && day < a.day + activitySpan(a);

// Where you're staying. A trip has one base (TripPlan.base*) plus optional
// per-day overrides keyed by day index (Lodging map).
export interface LodgingPoint {
  location: string;
  lat: number | null;
  lng: number | null;
}
export type LodgingMap = Record<string, LodgingPoint>; // key = day index as string

export interface TripPlan {
  id: string;
  uid: string;
  title: string;
  destination: string;
  imageUrl?: string; // cover image (pasted URL) shown on the planner landing card
  startDate: string; // ISO date string ("")
  endDate: string; // ISO date string ("")
  activities: Activity[];
  baseLocation?: string; // trip-wide accommodation base
  baseLat?: number | null;
  baseLng?: number | null;
  lodging?: LodgingMap; // per-day overrides
  sharedWith?: string[]; // lowercased emails this plan is shared with (collaborators)
  persons?: number; // party size — used to split the budget per person
  currency?: string; // budget currency label, e.g. "kr", "€"
  status?: TripStatus; // wish / planned / done
  createdAt?: any;
}

// Normalize an email for sharing comparisons/storage.
export const normEmail = (e: string): string => (e || "").trim().toLowerCase();
export const isValidEmail = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

// icon (emoji, no extra deps) + accent colour per activity type
export const ACTIVITY_TYPES: Record<ActivityType, { label: string; icon: string; color: string }> = {
  hike: { label: "Vandring", icon: "🥾", color: "#4caf72" },
  food: { label: "Mat", icon: "🍽️", color: "#e2872f" },
  hotel: { label: "Hotell", icon: "🏨", color: "#c0566b" },
  travel: { label: "Bil/Resa", icon: "🚗", color: "#3d8bcd" },
  flight: { label: "Flyg", icon: "✈️", color: "#56a0d3" },
  train: { label: "Tåg", icon: "🚆", color: "#c56b3a" },
  boat: { label: "Båt", icon: "⛴️", color: "#2a9d8f" },
  sightseeing: { label: "Sevärt", icon: "📸", color: "#9b6dd6" },
  beach: { label: "Strand", icon: "🏖️", color: "#1fb6b0" },
  nightlife: { label: "Nöje", icon: "🍸", color: "#d6588f" },
  rest: { label: "Vila", icon: "😌", color: "#7d8794" },
  other: { label: "Annat", icon: "📍", color: "#b0b6bf" },
};

export const ACTIVITY_TYPE_ORDER: ActivityType[] = [
  "hike", "food", "hotel", "travel", "flight", "train", "boat", "sightseeing", "beach", "nightlife", "rest", "other",
];

// Types that have a start AND an end point (drawn as start→🏁 + a line on the map).
export const ROUTE_TYPES: ActivityType[] = ["hike", "travel", "flight", "train", "boat"];
export const hasRoute = (t: ActivityType): boolean => ROUTE_TYPES.includes(t);

// Point-to-point transport legs (drive/flight/train/boat): the trip playback
// animates a moving vehicle along these, and the map draws them base→destination.
export const JOURNEY_TYPES: ActivityType[] = ["travel", "flight", "train", "boat"];
export const isJourney = (t: ActivityType): boolean => JOURNEY_TYPES.includes(t);

// Emoji used for the moving playback marker (and a small arc for flights).
export const VEHICLE_GLYPH: Partial<Record<ActivityType, string>> = {
  flight: "✈️", train: "🚆", boat: "⛴️", travel: "🚗",
};

// Booking status metadata: label + badge (badge null = don't surface on cards).
export const BOOKING_STATUS: Record<BookingStatus, { label: string; badge: string | null; color: string }> = {
  none: { label: "Ingen bokning", badge: null, color: "#7d8794" },
  maybe: { label: "Boka ibland", badge: "⚠️", color: "#e2b31a" },
  required: { label: "Bokning krävs", badge: "🎫", color: "#d6588f" },
};

export const BOOKING_ORDER: BookingStatus[] = ["none", "maybe", "required"];

// Types that always need booking (hotels + flights), even if the user left the
// booking field on "none" — they always surface in the Boka tab as "required".
export const AUTO_BOOKING_TYPES: ActivityType[] = ["hotel", "flight"];
export const effectiveBooking = (a: { type: ActivityType; booking: BookingStatus }): BookingStatus =>
  a.booking !== "none" ? a.booking : AUTO_BOOKING_TYPES.includes(a.type) ? "required" : "none";

// Classify a pasted URL so the UI can show a recognizable label/icon.
export const linkKind = (url: string): { icon: string; label: string } => {
  const u = (url || "").toLowerCase();
  if (u.includes("alltrails.")) return { icon: "🥾", label: "AllTrails" };
  if (u.includes("komoot.")) return { icon: "🥾", label: "Komoot" };
  if (u.includes("youtube.") || u.includes("youtu.be")) return { icon: "▶️", label: "YouTube" };
  if (u.includes("google.") && u.includes("maps")) return { icon: "🗺️", label: "Karta" };
  if (u.includes("instagram.")) return { icon: "📸", label: "Instagram" };
  return { icon: "🔗", label: "Länk" };
};

// AllTrails search URL prefilled from a hike's name + destination.
export const allTrailsSearchUrl = (...terms: string[]): string =>
  "https://www.alltrails.com/search?q=" +
  encodeURIComponent(terms.map((t) => (t || "").trim()).filter(Boolean).join(" "));

// Parse a pasted AllTrails share blob, e.g.
//   "Lake Sorapis - Forcella Marcoira via Passo Tre Croci on AllTrails\nhttps://…"
// into the trail link + name and best-effort start/end place names. AllTrails
// titles lead with the headline destination and append "via <trailhead>", so we
// treat "via <X>" as the start (trailhead) and the FIRST named feature as the
// goal/end. Without a "via", it's read as point-to-point ("<A> to <B>").
export const parseAllTrailsShare = (
  text: string
): { url: string; title: string; start?: string; end?: string } | null => {
  const m = (text || "").match(/https?:\/\/[^\s]*alltrails\.[^\s]*/i);
  if (!m) return null;
  const url = m[0];
  let title = (text.split(url)[0] || "")
    .replace(/\s*on AllTrails\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!title) {
    // fall back to the URL slug, e.g. ".../passo-tre-croci-lago-di-sorapis"
    const slug = decodeURIComponent(url.split("?")[0].split("/").filter(Boolean).pop() || "");
    title = slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  let t = title;
  let via = "";
  const vm = t.match(/\s+via\s+(.+)$/i);
  if (vm) { via = vm[1].trim(); t = t.slice(0, vm.index).trim(); }
  const parts = t.split(/\s+(?:-|–|—|to|till)\s+/i).map((s) => s.trim()).filter(Boolean);
  const start = via || parts[0] || undefined;
  // with a trailhead, the headline feature (first) is the goal; otherwise the
  // last name is the destination of a point-to-point route.
  const end = via ? parts[0] : parts.length > 1 ? parts[parts.length - 1] : undefined;
  return { url, title, start, end };
};

// Minutes-of-day parsed from an activity's free-text time ("09:00", "kl 9.30",
// "Morgon"…). Unparseable/blank → sorts last.
export const activityMinutes = (a: { time?: string }): number => {
  const m = /(\d{1,2})[:.](\d{2})/.exec(a.time || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.POSITIVE_INFINITY;
};
// Comparator to order a day's activities by time (timed first, then untimed).
export const byTime = (a: { time?: string }, b: { time?: string }): number =>
  activityMinutes(a) - activityMinutes(b);

export const newId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const emptyActivity = (): Activity => ({
  id: newId(),
  title: "",
  type: "other",
  time: "",
  location: "",
  lat: null,
  lng: null,
  endLocation: "",
  endLat: null,
  endLng: null,
  stops: [],
  travelTime: "",
  travelAuto: false,
  travelFromOther: false,
  originLocation: "",
  originLat: null,
  originLng: null,
  parking: "",
  booking: "none",
  bookingInfo: "",
  booked: false,
  flightNumber: "",
  arrTime: "",
  link: "",
  notes: "",
  day: null,
  span: 1,
  price: 0,
});

// Inclusive number of days between two ISO dates; 0 if invalid/missing.
export const dayCount = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;
  const s = new Date(startDate);
  const e = new Date(endDate);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
  const diff = Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
  return diff > 0 ? diff : 0;
};

// Parse an ISO date as a *local* date (avoids the UTC-midnight off-by-one
// you get from `new Date("YYYY-MM-DD")` in negative-offset timezones).
export const parseISO = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

export const addDaysISO = (iso: string, n: number): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const normalizeActivity = (a: any): Activity => ({
  id: a?.id ?? newId(),
  title: a?.title ?? "",
  type: a?.type ?? "other",
  time: a?.time ?? "",
  location: a?.location ?? "",
  lat: typeof a?.lat === "number" ? a.lat : null,
  lng: typeof a?.lng === "number" ? a.lng : null,
  endLocation: a?.endLocation ?? "",
  endLat: typeof a?.endLat === "number" ? a.endLat : null,
  endLng: typeof a?.endLng === "number" ? a.endLng : null,
  stops: Array.isArray(a?.stops)
    ? a.stops.map((s: any) => ({
        location: s?.location ?? "",
        lat: typeof s?.lat === "number" ? s.lat : null,
        lng: typeof s?.lng === "number" ? s.lng : null,
      }))
    : [],
  travelTime: a?.travelTime ?? "",
  travelAuto: a?.travelAuto === true,
  travelFromOther: a?.travelFromOther === true,
  originLocation: a?.originLocation ?? "",
  originLat: typeof a?.originLat === "number" ? a.originLat : null,
  originLng: typeof a?.originLng === "number" ? a.originLng : null,
  parking: a?.parking ?? "",
  booking: (["none", "maybe", "required"].includes(a?.booking) ? a.booking : "none") as BookingStatus,
  bookingInfo: a?.bookingInfo ?? "",
  booked: a?.booked === true,
  flightNumber: a?.flightNumber ?? "",
  arrTime: a?.arrTime ?? "",
  link: a?.link ?? "",
  notes: a?.notes ?? "",
  day: typeof a?.day === "number" ? a.day : null,
  span: typeof a?.span === "number" && a.span > 1 ? Math.floor(a.span) : 1,
  price: typeof a?.price === "number" && a.price > 0 ? a.price : 0,
});

// Read activities from a stored doc, migrating the legacy `days[]` shape
// (activities nested under each day) into the flat list with a `day` index.
export const readActivities = (data: any): Activity[] => {
  if (Array.isArray(data?.activities)) return data.activities.map(normalizeActivity);
  if (Array.isArray(data?.days)) {
    const out: Activity[] = [];
    data.days.forEach((d: any, i: number) =>
      (d?.activities ?? []).forEach((a: any) => out.push({ ...normalizeActivity(a), day: i }))
    );
    return out;
  }
  return [];
};

// Day count for display, falling back to the legacy `days[]` length.
export const readDayCount = (data: any): number => {
  const dc = dayCount(data?.startDate ?? "", data?.endDate ?? "");
  if (dc > 0) return dc;
  if (Array.isArray(data?.days)) return data.days.length;
  return 0;
};
